/**
 * Utility to map chatbot configuration between the API (snake_case)
 * and the application's internal structure (camelCase).
 */

export function mapApiToInternalConfig(apiData: any, defaultConfig: any): any {
  if (!apiData) return defaultConfig;

  const layout = apiData.layout_settings || {};
  const theme = apiData.color_palette || {};
  const toggle = apiData.toggle_and_branding || {};
  const copy = apiData.text_context || {};
  const behavior = apiData.behavior || {};

  return {
    layout: {
      iframe: {
        widthPx: layout.iframe_width_px ?? defaultConfig.layout.iframe.widthPx,
        heightPx: layout.iframe_height_px ?? defaultConfig.layout.iframe.heightPx,
      },
      floating: {
        edgeOffsetPx: layout.floating_edge_offset_px ?? defaultConfig.layout.floating.edgeOffsetPx,
        stackGapPx: layout.floating_stack_gap_px ?? defaultConfig.layout.floating.stackGapPx,
      },
      motion: {
        panelEnterSec: layout.motion_panel_enter_sec ?? defaultConfig.layout.motion.panelEnterSec,
        messageStaggerSec: layout.motion_message_stagger_sec ?? defaultConfig.layout.motion.messageStaggerSec,
        messageFadeSec: layout.motion_message_fade_sec ?? defaultConfig.layout.motion.messageFadeSec,
        typingDotPulseSec: layout.motion_typing_dot_pulse_sec ?? defaultConfig.layout.motion.typingDotPulseSec,
        sidebarWidthPx: layout.motion_sidebar_width_px ?? defaultConfig.layout.motion.sidebarWidthPx,
      },
      chatPanel: {
        widthPx: layout.chatpanel_width_px ?? defaultConfig.layout.chatPanel.widthPx,
        widthCompactPx: layout.chatpanel_width_compact_px ?? defaultConfig.layout.chatPanel.widthCompactPx,
        heightPx: layout.chatpanel_height_px ?? defaultConfig.layout.chatPanel.heightPx,
        fullScreen: layout.chatpanel_fullscreen ?? defaultConfig.layout.chatPanel.fullScreen,
        overlayZIndex: layout.chatpanel_overlay_zindex ?? defaultConfig.layout.chatPanel.overlayZIndex,
      },
    },
    theme: {
      primary: theme.primary ?? defaultConfig.theme.primary,
      text: theme.text ?? defaultConfig.theme.text,
      onPrimary: theme.on_primary ?? defaultConfig.theme.onPrimary,
      surface: theme.surface ?? defaultConfig.theme.surface,
      surfaceMuted: theme.surface_muted ?? defaultConfig.theme.surfaceMuted,
      border: theme.border ?? defaultConfig.theme.border,
      muted: theme.muted ?? defaultConfig.theme.muted,
      headerBorderBottom: theme.header_border_bottom ?? defaultConfig.theme.headerBorderBottom,
    },
    toggle: {
      sizePx: toggle.size_px ?? defaultConfig.toggle.sizePx,
      iconName: toggle.icon_name ?? defaultConfig.toggle.iconName,
      iconCloseName: toggle.icon_close_name ?? defaultConfig.toggle.iconCloseName,
      iconSize: toggle.icon_size_px ?? defaultConfig.toggle.iconSize,
      backgroundColor: toggle.background_color ?? defaultConfig.toggle.backgroundColor,
      textColor: toggle.text_color ?? defaultConfig.toggle.textColor,
      botName: toggle.bot_name ?? defaultConfig.toggle.botName,
      horizontalPaddingPx: toggle.horizontal_padding_px ?? defaultConfig.toggle.horizontalPaddingPx,
    },
    copy: {
      headerTitle: copy.header_title ?? defaultConfig.copy.headerTitle,
      headerSubtitle: copy.header_subtitle ?? defaultConfig.copy.headerSubtitle,
      greeting: copy.greeting ?? defaultConfig.copy.greeting,
      emptyStateSubtext: copy.empty_state_subtext ?? defaultConfig.copy.emptyStateSubtext,
      inputPlaceholder: copy.input_placeholder ?? defaultConfig.copy.inputPlaceholder,
      sendAriaLabel: copy.send_aria_label ?? defaultConfig.copy.sendAriaLabel,
      newChatLabel: copy.new_chat_label ?? defaultConfig.copy.newChatLabel,
      sessionsHeading: copy.sessions_heading ?? defaultConfig.copy.sessionsHeading,
      missingBotIdMessage: copy.missing_bot_id_message ?? defaultConfig.copy.missingBotIdMessage,
    },
    behavior: {
      showGreetingWhenEmpty: behavior.show_greeting_when_empty ?? defaultConfig.behavior.showGreetingWhenEmpty,
      showHeaderSubtitle: behavior.show_header_subtitle ?? defaultConfig.behavior.showHeaderSubtitle,
      showHeaderMaximize: behavior.show_header_maximize ?? defaultConfig.behavior.showHeaderMaximize,
      suggestionPrompts: behavior.suggestion_prompts ?? defaultConfig.behavior.suggestionPrompts,
    },
  };
}

export function mapInternalToApiConfig(config: any, chatbotId: number, source: string): any {
  return {
    chatbot_id: chatbotId,
    layout_settings: {
      iframe_width_px: config.layout.iframe.widthPx,
      iframe_height_px: config.layout.iframe.heightPx,
      floating_edge_offset_px: config.layout.floating.edgeOffsetPx,
      floating_stack_gap_px: config.layout.floating.stackGapPx,
      motion_panel_enter_sec: config.layout.motion.panelEnterSec,
      motion_message_stagger_sec: config.layout.motion.messageStaggerSec,
      motion_message_fade_sec: config.layout.motion.messageFadeSec,
      motion_typing_dot_pulse_sec: config.layout.motion.typingDotPulseSec,
      motion_sidebar_width_px: config.layout.motion.sidebarWidthPx,
      chatpanel_width_px: config.layout.chatPanel.widthPx,
      chatpanel_width_compact_px: config.layout.chatPanel.widthCompactPx,
      chatpanel_height_px: config.layout.chatPanel.heightPx,
      chatpanel_fullscreen: config.layout.chatPanel.fullScreen,
      chatpanel_overlay_zindex: config.layout.chatPanel.overlayZIndex,
    },
    color_palette: {
      primary: config.theme.primary,
      text: config.theme.text,
      on_primary: config.theme.onPrimary,
      surface: config.theme.surface,
      surface_muted: config.theme.surfaceMuted,
      border: config.theme.border,
      muted: config.theme.muted,
      header_border_bottom: config.theme.headerBorderBottom,
    },
    toggle_and_branding: {
      size_px: config.toggle.sizePx,
      icon_name: config.toggle.iconName,
      icon_close_name: config.toggle.iconCloseName,
      icon_size_px: config.toggle.iconSize,
      background_color: config.toggle.backgroundColor,
      text_color: config.toggle.textColor,
      bot_name: config.toggle.botName,
      horizontal_padding_px: config.toggle.horizontalPaddingPx,
    },
    text_context: {
      header_title: config.copy.headerTitle,
      header_subtitle: config.copy.headerSubtitle,
      greeting: config.copy.greeting,
      empty_state_subtext: config.copy.emptyStateSubtext,
      input_placeholder: config.copy.inputPlaceholder,
      send_aria_label: config.copy.sendAriaLabel,
      new_chat_label: config.copy.newChatLabel,
      sessions_heading: config.copy.sessionsHeading,
      missing_bot_id_message: config.copy.missingBotIdMessage,
    },
    behavior: {
      show_greeting_when_empty: config.behavior.showGreetingWhenEmpty,
      show_header_subtitle: config.behavior.showHeaderSubtitle,
      show_header_maximize: config.behavior.showHeaderMaximize,
      suggestion_prompts: config.behavior.suggestionPrompts,
    },
  };
}
